import type { DashboardDetail, ReportDetail, Tile } from "@/api/queries";
import type { Link } from "@/artifacts/links";
import type { Artifact as ArtifactType } from "@/artifacts/types";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import { useInteractiveDocument } from "@/components/artifacts/documentMode";
import { LinkedScope } from "@/components/artifacts/LinkedScope";
import { useGridScope } from "@/components/reports/gridScope";
import { Markdown } from "@/components/chat/Markdown";
import { PrintLayout, PrintSection, SqlAppendix } from "@/print/PrintLayout";
import { tileSections } from "@/print/tiles";

/**
 * A report, a dashboard or one tile as a *document* (FR-9.1, HTML-1).
 *
 * Two hosts render these: the print route — which is the PDF — and the
 * standalone bundle inside an exported HTML file or a live embed. Data comes
 * in as props; neither component fetches, so the same tree draws the same
 * numbers whether a headless browser is printing it or somebody opened it
 * from their downloads folder. A second copy of this layout is how the PDF
 * and the HTML file would come to disagree about which tiles failed.
 */

/** A tile or an answer artifact, printed. */
export function PrintArtifact({
  artifact,
  title,
  note,
  linkIndex,
}: {
  artifact: ArtifactType;
  title?: string;
  note?: string;
  /** Its position in the document's links, in a live document only (LINK-6). */
  linkIndex?: number;
}) {
  return (
    <div className="print-block mb-4">
      <ArtifactCard
        artifact={title ? { ...artifact, title } : artifact}
        csvUrl=""
        chrome={false}
        linkIndex={linkIndex}
        subtitle={
          note ? <span className="type-caption text-warning normal-case">{note}</span> : undefined
        }
      />
    </div>
  );
}

/**
 * A document's tiles: printed as they are, or — in an exported file or an
 * embed, open in a browser — as one linked scope (LINK-6). Nothing here can
 * call back, so the scope reads only what the export built ahead, and a
 * capped table filters the rows it carries.
 */
function DocumentTiles({ tiles, links }: { tiles: Tile[]; links: Link[] }) {
  const live = useInteractiveDocument() && links.length > 0;
  const scope = useGridScope(tiles, { canFetch: false, offline: true });
  const cards = tiles.map((tile) => (
    <PrintArtifact
      key={tile.id}
      artifact={tile.artifact}
      title={tile.title}
      note={staleNote(tile)}
      linkIndex={live ? scope.indexOf(tile.id) : undefined}
    />
  ));
  if (!live) return <>{cards}</>;
  return (
    <LinkedScope
      links={links}
      view={scope.view}
      zoom={scope.zoom}
      label="Filter these tiles"
      barClassName="mb-4"
    >
      {cards}
    </LinkedScope>
  );
}

function staleNote(tile: Tile): string | undefined {
  if (tile.refresh_status !== "error" && tile.refresh_status !== "blocked") return undefined;
  return `Could not be refreshed${tile.refresh_error ? ` — ${tile.refresh_error}` : ""}. These are the last values that worked.`;
}

export function ReportDocument({ report, sql }: { report: ReportDetail; sql: boolean }) {
  const tiles = tileSections(report.tiles);
  const failures = tiles.filter(
    (tile) => tile.refresh_status === "error" || tile.refresh_status === "blocked",
  );

  return (
    <PrintLayout
      title={report.title}
      subtitle={report.question ? `“${report.question}”` : undefined}
      asOf={report.last_refreshed_at}
      facts={[
        ["Source", report.connection_name],
        ["Model", report.model_label],
      ].filter((pair): pair is [string, string] => Boolean(pair[1]))}
    >
      {report.summary ? (
        <PrintSection>
          <div className="type-body text-ink">
            <Markdown>{report.summary}</Markdown>
          </div>
        </PrintSection>
      ) : null}

      {report.insights.length ? (
        <PrintSection title="Insights">
          <ul className="type-support text-ink-secondary list-disc pl-5">
            {report.insights.map((insight) => (
              <li key={insight} className="mb-1">
                {insight}
              </li>
            ))}
          </ul>
        </PrintSection>
      ) : null}

      {failures.length ? (
        <p className="bg-warning-subtle text-warning type-support rounded-element mb-4 px-3 py-2">
          {failures.length} tile{failures.length === 1 ? "" : "s"} could not be refreshed. The
          values below are the last ones that worked.
        </p>
      ) : null}

      <DocumentTiles tiles={tiles} links={report.links ?? []} />

      {report.sources.length ? (
        <PrintSection title="Sources">
          <p className="type-support text-ink-secondary">{report.sources.join(" · ")}</p>
        </PrintSection>
      ) : null}

      {sql ? (
        <SqlAppendix
          queries={report.pipeline.map((entry, index) => ({
            index: Number(entry.query_index ?? index + 1),
            sql: String(entry.sql ?? ""),
            connection: report.connection_name,
            rows: typeof entry.row_count === "number" ? entry.row_count : null,
          }))}
        />
      ) : null}
    </PrintLayout>
  );
}

export function DashboardDocument({
  dashboard,
  sql,
}: {
  dashboard: DashboardDetail;
  sql: boolean;
}) {
  const tiles = tileSections(dashboard.tiles);
  // The same dedup a refresh does, so the appendix agrees with the audit log:
  // six tiles over three statements print three queries, not six.
  const seen = new Set<string>();
  const queries = tiles
    .filter((tile) => {
      const key = `${tile.connection_id ?? ""}|${tile.sql.replace(/\s+/g, " ").trim()}`;
      if (!tile.sql.trim() || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((tile, index) => ({
      index: index + 1,
      sql: tile.sql,
      connection: tile.connection_name,
      rows: tile.row_count,
    }));

  return (
    <PrintLayout
      title={dashboard.name}
      subtitle={dashboard.description || undefined}
      asOf={dashboard.last_refreshed_at}
      facts={
        dashboard.connections.length ? [["Sources", dashboard.connections.join(", ")]] : undefined
      }
    >
      <DocumentTiles tiles={tiles} links={dashboard.links ?? []} />
      {sql ? <SqlAppendix queries={queries} /> : null}
    </PrintLayout>
  );
}

/** One tile on its own: the card, its owner named, and its own query. */
export function TileDocument({
  tile,
  owner,
  asOf,
  sql,
}: {
  tile: Tile;
  /** The report or dashboard it belongs to, by name. */
  owner: string;
  asOf: string | null | undefined;
  sql: boolean;
}) {
  return (
    <PrintLayout
      title={tile.title || owner}
      subtitle={`From ${owner}`}
      asOf={asOf}
      facts={tile.connection_name ? [["Source", tile.connection_name]] : undefined}
    >
      <PrintArtifact artifact={tile.artifact} title={tile.title} note={staleNote(tile)} />
      {sql && tile.sql.trim() ? (
        <SqlAppendix
          queries={[
            {
              index: 1,
              sql: tile.sql,
              connection: tile.connection_name ?? undefined,
              rows: tile.row_count,
            },
          ]}
        />
      ) : null}
    </PrintLayout>
  );
}
